import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { findCourseBySlug, getLessonUrl, getLessonNumber, courseNameToSlug } from '../utils/urlUtils';
import { useProgress } from '../contexts/ProgressContext';
import { useAuth } from '../contexts/AuthContext';
import { useReward } from '../contexts/RewardContext';
import { useTheme } from '../contexts/ThemeContext';
import AnalyticsService from '../services/analyticsService';
import Header from './Header';
import Breadcrumb from './Breadcrumb';
import { BodyMedium, BodyLarge, TitleLarge, HeadlineMedium, HeadlineSmall } from './Typography';
import { renderMarkdownToHTML } from '../utils/markdownRenderer';
import ActionButton from './ActionButton';
import LessonVideoPlayer from './LessonVideoPlayer';
import { isVimeoUrl } from '../utils/vimeoUrl';
// Removed custom checkbox imports - now using native HTML checkboxes
import HexagonDarkGrey from '../images/hexagon_darkgrey.png';
import HexagonBlue from '../images/hexagon_blue.png';
import HexagonComplete from '../images/lesson_complete.png';
import HexagonCompleteActive from '../images/lesson_complete_active.png';

const Lesson = ({ onNavigateToHome, onNavigateToProfile, onNavigateToPrompts, onNavigateToWritersRoom, onLogOut, onNavigateToCourses, onStartLesson, onModeSwitch, showAdminMode = false }) => {
  const { courseSlug, levelNumber, lessonNumber } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lessonTransitioning, setLessonTransitioning] = useState(false);
  const [nextLesson, setNextLesson] = useState(null);
  const [levelLessons, setLevelLessons] = useState([]);
  const [allCourseLessons, setAllCourseLessons] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const hasTrackedAccess = useRef(false);
  /* Refs used to manage focus when the graphic lightbox opens and closes */
  const lightboxTriggerRef = useRef(null);
  const lightboxCloseRef = useRef(null);


  const { 
    getLessonProgress, 
    updateProgress, 
    isLessonCompleted, 
    getLessonStatus,
    canAccessLesson
  } = useProgress();

  const { user, currentSession, userOrganizations } = useAuth();
  const { showLevelComplete, showCourseComplete } = useReward();
  const { themeColors } = useTheme();

  // Lightbox handlers
  const openLightbox = () => setLightboxOpen(true);
  const closeLightbox = () => {
    setLightboxOpen(false);
    /* Return keyboard focus to the element that triggered the lightbox */
    setTimeout(() => {
      if (lightboxTriggerRef.current) lightboxTriggerRef.current.focus();
    }, 0);
  };

  // Handle escape key for lightbox
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && lightboxOpen) {
        closeLightbox();
      }
    };

    if (lightboxOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when lightbox is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [lightboxOpen]);

  /* Move focus into the lightbox close button when the dialog opens */
  useEffect(() => {
    if (lightboxOpen && lightboxCloseRef.current) {
      lightboxCloseRef.current.focus();
    }
  }, [lightboxOpen]);

  // Computed values for backward compatibility
  const courseId = course?.id;
  const levelId = parseInt(levelNumber) || null;
  const lessonId = lesson?.id;

  const fetchAllCourses = useCallback(async () => {
    try {
      const coursesRef = collection(db, 'courses');
      const coursesSnapshot = await getDocs(coursesRef);
      const courses = coursesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAllCourses(courses);
      return courses;
    } catch (error) {
      setAllCourses([]);
      return [];
    }
  }, []);

  const fetchLevelLessons = useCallback(async (courseId, level) => {
    try {
      const lessonsRef = collection(db, 'lessons');
      const levelQuery = query(
        lessonsRef,
        where('course_id', '==', courseId),
        where('level', '==', level)
      );
      
      const levelSnapshot = await getDocs(levelQuery);
      const lessons = levelSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort lessons by lesson_number
      lessons.sort((a, b) => a.lesson_number - b.lesson_number);
      setLevelLessons(lessons);
      return lessons;
      
    } catch (error) {
      // EFFICIENT: Silent error handling, component stays functional
      setLevelLessons([]);
      return [];
    }
  }, []);

  const getAllCourseLessons = useCallback(async (courseId) => {
    try {
      const lessonsRef = collection(db, 'lessons');
      const allLevelsQuery = query(
        lessonsRef,
        where('course_id', '==', courseId)
      );
      
      const allLevelsSnapshot = await getDocs(allLevelsQuery);
      const allLessons = allLevelsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setAllCourseLessons(allLessons);
      return allLessons;
    } catch (error) {
      return [];
    }
  }, []);

  const findNextLesson = useCallback(async (currentLesson) => {
    try {
      const lessonsRef = collection(db, 'lessons');
      
      // First, check if there's a next lesson in the same level
      const sameLevelQuery = query(
        lessonsRef,
        where('course_id', '==', courseId),
        where('level', '==', currentLesson.level),
        where('lesson_number', '==', currentLesson.lesson_number + 1)
      );
      
      const sameLevelSnap = await getDocs(sameLevelQuery);
      if (!sameLevelSnap.empty) {
        // Found next lesson in same level
        setNextLesson({ id: sameLevelSnap.docs[0].id, ...sameLevelSnap.docs[0].data() });
        return;
      }
      
      // If no next lesson in same level, check for first lesson of next level
      const nextLevelQuery = query(
        lessonsRef,
        where('course_id', '==', courseId),
        where('level', '==', currentLesson.level + 1),
        where('lesson_number', '==', 1)
      );
      
      const nextLevelSnap = await getDocs(nextLevelQuery);
      if (!nextLevelSnap.empty) {
        setNextLesson({ id: nextLevelSnap.docs[0].id, ...nextLevelSnap.docs[0].data() });
      }
    } catch (error) {
      // SIMPLE: If next lesson can't be found, navigation will handle gracefully
      setNextLesson(null);
    }
  }, [courseId]);


  // Initialize course and level data from URL parameters (full initialization)
  useEffect(() => {
    const initializeCourseAndLevel = async () => {
      if (!courseSlug || !levelNumber) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        // Fetch courses and find the matching one
        const courses = await fetchAllCourses();
        const matchedCourse = findCourseBySlug(courses, courseSlug);
        
        if (matchedCourse) {
          setCourse(matchedCourse);
          
          // Get lessons for this level
          const level = parseInt(levelNumber);
          const lessons = await fetchLevelLessons(matchedCourse.id, level);
          
          // Get all course lessons for completion checking
          await getAllCourseLessons(matchedCourse.id);
          
          // Note: Don't set the specific lesson here - that's handled by the lesson-specific effect
        } else {
          setCourse(null);
          setLesson(null);
        }
      } catch (error) {
        console.error('Error initializing course/level from URL:', error);
        setCourse(null);
        setLesson(null);
      } finally {
        setLoading(false);
      }
    };
    
    initializeCourseAndLevel();
  }, [courseSlug, levelNumber, fetchAllCourses, fetchLevelLessons]);

  // Handle lesson-specific changes (optimized for same-level navigation)
  useEffect(() => {
    const updateLessonOnly = async () => {
      if (!lessonNumber || !course || levelLessons.length === 0) {
        return;
      }
      
      try {
        setLessonTransitioning(true);
        
        // Find the specific lesson by number (1-based index) from already loaded levelLessons
        const lessonIndex = parseInt(lessonNumber) - 1;
        const targetLesson = levelLessons[lessonIndex] || levelLessons[0]; // Fallback to first lesson
        
        setLesson(targetLesson);
        
        // Find the next lesson for navigation
        await findNextLesson(targetLesson);
        
        // Small delay to show smooth transition
        setTimeout(() => setLessonTransitioning(false), 100);
      } catch (error) {
        console.error('Error updating lesson:', error);
        setLessonTransitioning(false);
      }
    };
    
    updateLessonOnly();
  }, [lessonNumber, levelLessons, course, findNextLesson]);

  const findNextAccessibleLesson = useCallback((allLessons) => {
    if (!allLessons || allLessons.length === 0) return null;
    
    // Sort lessons by level, then by lesson_number
    const sortedLessons = [...allLessons].sort((a, b) => {
      if (a.level !== b.level) return a.level - b.level;
      return a.lesson_number - b.lesson_number;
    });

    // Find first lesson that can be accessed
    for (const lesson of sortedLessons) {
      if (canAccessLesson(lesson.id, allLessons)) {
        return lesson;
      }
    }
    
    // If no accessible lessons found, it means all lessons are completed
    // Try to find the first lesson of the next level
    const currentLevel = levelId;
    const nextLevel = currentLevel + 1;
    
    // Look for first lesson of next level
    const nextLevelLesson = sortedLessons.find(lesson => 
      lesson.level === nextLevel && lesson.lesson_number === 1
    );
    if (nextLevelLesson) {
      return nextLevelLesson;
    }
    
    // If no next level, or if we're at the highest level, return first lesson of course
    if (sortedLessons.length > 0) {
      return sortedLessons[0];
    }
    
    return null;
  }, [canAccessLesson, levelId]);

  // Fetch lesson data
  // Old lesson fetching logic removed - now handled in URL-based initialization

  // Reset tracking flag when lessonId changes
  useEffect(() => {
    hasTrackedAccess.current = false;
  }, [lessonId]);

  // Track lesson access analytics when a lesson is opened
  useEffect(() => {
    const trackLessonAccess = async () => {
      if (
        hasTrackedAccess.current ||
        !lessonId ||
        !user ||
        !currentSession ||
        !userOrganizations?.length
      ) {
        return;
      }

      hasTrackedAccess.current = true;

      await AnalyticsService.trackAccess(
        'lesson',
        lessonId,
        currentSession,
        user.uid,
        userOrganizations,
        {
          courseId,
          levelNumber: levelId,
          lessonNumber: lesson?.lesson_number,
          lessonTitle: lesson?.title || lesson?.headline
        }
      );
    };

    trackLessonAccess();
  }, [lessonId, user, currentSession, userOrganizations, courseId, levelId, lesson]);

  const handleNextStep = async () => {
    if (nextLesson && canProceed) {
      // Check if this lesson was already completed before clicking Next Step
      const wasAlreadyCompleted = isLessonCompleted(lessonId);
      
      // Only mark current lesson as completed if it's not already completed
      if (!wasAlreadyCompleted) {
        updateProgress(lessonId, { status: 'completed', completed: true }, 'lesson');
        // Add a small delay to ensure the progress update is processed
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Only show rewards and track analytics if this lesson was just completed (not already completed)
      if (!wasAlreadyCompleted) {
        // Check if current level is now completed
        // First, fetch the current level's lessons to ensure we have the correct data
        const currentLevelLessons = await fetchLevelLessons(courseId, levelId);
        
        // Sort lessons by lesson_number to find the last lesson
        const sortedLessons = [...currentLevelLessons].sort((a, b) => a.lesson_number - b.lesson_number);
        const lastLessonInLevel = sortedLessons[sortedLessons.length - 1];
        const isLastLesson = lastLessonInLevel && lastLessonInLevel.id === lessonId;
        
        // Count completed lessons: include current lesson (just completed) + previously completed lessons
        const completedLessonsInLevel = currentLevelLessons.filter(lesson => 
          lesson.id === lessonId || isLessonCompleted(lesson.id)
        );
        
        // Check if this was the last lesson in the course (course completed) - CHECK THIS FIRST
        let courseCompleted = false;
        if (allCourseLessons && allCourseLessons.length > 0) {
          const completedLessons = allCourseLessons.filter(lesson => 
            lesson.id === lessonId || isLessonCompleted(lesson.id)
          );
          
          if (completedLessons.length === allCourseLessons.length) {
            courseCompleted = true;
            
            // All lessons completed - show course completion reward
            showCourseComplete(course?.title || 'Course', {
              customMessage: `${course?.title || 'Course'} Course Completed!`
            });
            
            // Track course completion analytics
            if (user && currentSession && userOrganizations) {
              await AnalyticsService.trackCompletion(
                'course',
                courseId,
                currentSession,
                user.uid,
                userOrganizations,
                {
                  courseId,
                  totalLessons: allCourseLessons.length,
                  completedLessons: completedLessons.length,
                  action: 'course_completed_via_lesson'
                }
              );
            }
          }
        }
        
        // Only trigger level completion reward if course is NOT completed
        if (!courseCompleted && isLastLesson && completedLessonsInLevel.length === currentLevelLessons.length && currentLevelLessons.length > 0) {
          // Level completed! Show reward popup
          showLevelComplete(levelId, {
            customMessage: `Congratulations! You've completed Level ${levelId}!`
          });
        }
        
        // Track lesson completion analytics
        if (user && currentSession && userOrganizations) {
          await AnalyticsService.trackCompletion(
            'lesson',
            lessonId,
            currentSession,
            user.uid,
            userOrganizations,
            {
              courseId,
              levelId,
              lessonTitle: lesson?.title,
              timeToComplete: Date.now() // Could be enhanced with actual time tracking
            }
          );
        }
        
        // If course is completed, navigate to home page
        if (courseCompleted) {
          // Navigate to /home instead of / to avoid admin auto-redirect
          navigate('/home');
          return;
        }
      }
      
      // Mark next lesson as active only if it's not already completed
      if (!isLessonCompleted(nextLesson.id)) {
        updateProgress(nextLesson.id, { status: 'active' }, 'lesson');
      }
      
      // Navigate to next lesson
      // Get the lesson number within the next level
      const nextLevelLessons = await fetchLevelLessons(courseId, nextLesson.level);
      const nextLessonNumber = getLessonNumber(nextLesson, nextLevelLessons);
      
      
      navigate(getLessonUrl(course, nextLesson.level, nextLessonNumber));
    }
  };

  const handleTaskToggle = async (taskIndex) => {
    const lessonProgress = getLessonProgress(lessonId) || {};
    const currentTodos = lessonProgress.todos || {};
    
    // Check if this task is being completed (not unchecked)
    const isCompletingTask = !currentTodos[taskIndex]?.completed;
    
    // Toggle the todo status
    const newTodos = {
      ...currentTodos,
      [taskIndex]: {
        completed: !currentTodos[taskIndex]?.completed,
        completedAt: !currentTodos[taskIndex]?.completed ? new Date().toISOString() : null
      }
    };
    
    // Update progress
    updateProgress(lessonId, { todos: newTodos }, 'lesson');
    
    // Track task completion analytics (only when completing, not unchecking)
    if (isCompletingTask && user && currentSession && userOrganizations) {
      await AnalyticsService.trackCompletion(
        'task',
        `${lessonId}_task_${taskIndex}`,
        currentSession,
        user.uid,
        userOrganizations,
        {
          courseId,
          levelId,
          lessonId,
          lessonTitle: lesson?.title,
          taskIndex,
          taskText: lesson?.todos?.[taskIndex] || 'Unknown task',
          action: 'task_completed'
        }
      );
    }
    
    // Check if all todos are now completed and this lesson is the last in the course
    const allTodosCompleted = lesson?.todos?.every((_, index) => 
      index === taskIndex ? newTodos[index]?.completed : currentTodos[index]?.completed
    );
    
    if (allTodosCompleted && allCourseLessons && allCourseLessons.length > 0) {
      // Check if this was the last lesson in the course
      const completedLessons = allCourseLessons.filter(lesson => 
        lesson.id === lessonId || isLessonCompleted(lesson.id)
      );
      
      if (completedLessons.length === allCourseLessons.length) {
        // All lessons completed - track course completion
        if (user && currentSession && userOrganizations) {
          await AnalyticsService.trackCompletion(
            'course',
            courseId,
            currentSession,
            user.uid,
            userOrganizations,
            {
              courseId,
              totalLessons: allCourseLessons.length,
              completedLessons: completedLessons.length,
              action: 'course_completed_via_todos'
            }
          );
        }
      }
    }
  };

  const isTaskCompleted = (taskIndex) => {
    const lessonProgress = getLessonProgress(lessonId);
    return lessonProgress?.todos?.[taskIndex]?.completed || false;
  };

  const getHexagonImage = (targetLessonId) => {
    const status = isLessonCompleted(targetLessonId);
    const isCurrentLesson = targetLessonId === lessonId;
    
    if (status && isCurrentLesson) {
      // Show active version when viewing a completed lesson
      return HexagonCompleteActive;
    }
    if (status) {
      // Show regular completed version for other completed lessons
      return HexagonComplete;
    }
    if (isCurrentLesson) return HexagonBlue;
    return HexagonDarkGrey;
  };

  const shouldShowNumber = (targetLessonId) => {
    const status = isLessonCompleted(targetLessonId);
    return !status;
  };

  const handleNavigateToCourses = () => {
    navigate('/courses');
  };

  const handleNavigateToCourseOverview = () => {
    if (course?.course_name) {
      const slug = courseNameToSlug(course.course_name);
      navigate(`/courses/${slug}`);
    } else {
      navigate('/courses');
    }
  };

  const handleLessonNavigation = async (targetLesson) => {
    if (targetLesson) {
      // Use the same logic as clickability determination
      const isCompleted = isLessonCompleted(targetLesson.id);
      const isCurrentTargetLesson = targetLesson.id === lessonId;
      const canAccess = allCourseLessons.length > 0 && canAccessLesson(targetLesson.id, allCourseLessons);
      
      // Calculate if this lesson should be accessible based on completed lessons
      const completedLessons = levelLessons.filter(l => isLessonCompleted(l.id));
      const highestCompletedLesson = completedLessons.length > 0 
        ? Math.max(...completedLessons.map(l => l.lesson_number))
        : 0;
      const isNextInSequence = targetLesson.lesson_number <= (highestCompletedLesson + 1);
      
      const laterLessonsUnlocked = levelLessons.some(l => 
        l.lesson_number > targetLesson.lesson_number && 
        (isLessonCompleted(l.id) || (allCourseLessons.length > 0 && canAccessLesson(l.id, allCourseLessons)))
      );
      
      const shouldAllowNavigation = isCompleted || isCurrentTargetLesson || canAccess || laterLessonsUnlocked || isNextInSequence;
      
      if (shouldAllowNavigation) {
        // Get the lesson number within the level
        const targetLessonNumber = getLessonNumber(targetLesson, levelLessons);
        navigate(getLessonUrl(course, targetLesson.level, targetLessonNumber));
      }
    }
  };

  if (loading) {
    return (
      /* role=status announces loading to screen readers (WCAG 4.1.3 Status Messages) */
      <div role="status" aria-live="polite" className="flex items-center justify-center py-12">
        <div className="text-center">
          <div aria-hidden="true" className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <div className="mt-4">
            <BodyMedium className="text-white">Loading lesson...</BodyMedium>
          </div>
        </div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <TitleLarge className="text-white mb-4">Lesson not found</TitleLarge>
          <button 
            onClick={onNavigateToCourses}
            className="px-6 py-3 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors"
          >
            Back to Courses
          </button>
        </div>
      </div>
    );
  }

  // Check if all todos are completed based on lesson's actual todo count
  const canProceed = (() => {
    if (!lesson?.todos || lesson.todos.length === 0) return true; // No todos = can proceed
    
    const lessonProgress = getLessonProgress(lessonId);
    if (!lessonProgress?.todos) return false; // No progress = can't proceed
    
    // Check if all lesson todos are completed
    for (let i = 0; i < lesson.todos.length; i++) {
      if (!lessonProgress.todos[i]?.completed) {
        return false; // Found an incomplete todo
      }
    }
    
    return true; // All todos completed
  })();

  return (
    <div>
      {/* Header */}
      <Header 
        title={course?.course_name || course?.title || 'Course'} 
        onNavigateToHome={onNavigateToHome}
        onNavigateToCourses={handleNavigateToCourses}
        onNavigateToProfile={onNavigateToProfile}
        onNavigateToPrompts={onNavigateToPrompts}
        onNavigateToWritersRoom={onNavigateToWritersRoom}
        onLogOut={onLogOut}
        onModeSwitch={onModeSwitch}
        showAdminMode={showAdminMode}
      />

      {/* Breadcrumbs and Progress Indicator Section */}
      <div className="w-full px-4 md:px-[90px] py-6 sticky top-0 z-40 border-b" style={{ backgroundColor: themeColors.lessonHeader, borderColor: themeColors.border }}>
        {/* Breadcrumbs */}
        <div className="pb-4">
          <Breadcrumb 
            items={[
              { 
                label: 'Home', 
                onClick: onNavigateToHome 
              },
              { 
                label: 'Courses', 
                onClick: handleNavigateToCourses 
              },
            { 
              label: course?.course_name || course?.title || 'Course',
              onClick: handleNavigateToCourseOverview 
            },
            { 
              label: `Level ${lesson?.level}` 
            },
            { 
              label: lesson?.title || 'Lesson' 
            }
            ]} 
          />
        </div>

        {/* Progress Indicator with Level/Title and Lesson Numbers */}
        <div>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          {/* Mobile: Stacked layout / Desktop: Level and Lesson Title */}
          <div className="flex flex-col">
            {/* Mobile: Level and Lesson on same line / Desktop: Just Level */}
            <div className="flex items-center justify-between md:justify-start mb-1">
              <div className="text-sm font-medium mb-0 md:mb-1" style={{ color: themeColors.textPrimary }}>LEVEL {lesson.level}</div>
              <div className="text-sm font-medium md:hidden" style={{ color: themeColors.textPrimary }}>
                LESSON {(() => {
                  // Find current lesson number in the level
                  const currentLessonIndex = levelLessons.findIndex(l => l.id === lessonId);
                  return currentLessonIndex >= 0 ? currentLessonIndex + 1 : 1;
                })()}/{levelLessons.length}
              </div>
            </div>
            <HeadlineMedium style={{ color: themeColors.textPrimary }}>{lesson.title}</HeadlineMedium>
          </div>

          {/* Desktop only - Lesson Numbers and Next Step Button */}
          <div className="hidden md:flex items-center justify-start space-x-4">
            {/* Progress Hexagons - Dynamically rendered based on actual lessons */}
            <div className="flex space-x-2">
              {levelLessons.map((levelLesson, index) => {
                const hexagonImage = getHexagonImage(levelLesson.id);
                const showNumber = shouldShowNumber(levelLesson.id);
                const isCurrentLesson = levelLesson.id === lessonId;
                
                // Determine if this lesson is clickable
                // Simple approach: Allow access to any lesson that has ever been accessible
                const isCompleted = isLessonCompleted(levelLesson.id);
                const canAccess = allCourseLessons.length > 0 && canAccessLesson(levelLesson.id, allCourseLessons);
                
                // A lesson is clickable if:
                // 1. It's completed (can go back)
                // 2. It's currently being viewed
                // 3. It's accessible according to progress rules
                // 4. OR it's within the range of lessons that should be accessible based on completed lessons
                
                // Find the highest completed lesson number in this level
                const completedLessons = levelLessons.filter(l => isLessonCompleted(l.id));
                const highestCompletedLesson = completedLessons.length > 0 
                  ? Math.max(...completedLessons.map(l => l.lesson_number))
                  : 0;
                
                // Allow access to the next lesson after the highest completed one
                const isNextInSequence = levelLesson.lesson_number <= (highestCompletedLesson + 1);
                
                const laterLessonsUnlocked = levelLessons.some(l => 
                  l.lesson_number > levelLesson.lesson_number && 
                  (isLessonCompleted(l.id) || (allCourseLessons.length > 0 && canAccessLesson(l.id, allCourseLessons)))
                );
                
                const isClickable = isCompleted || isCurrentLesson || canAccess || laterLessonsUnlocked || isNextInSequence;
                const isFutureLesson = !isClickable; // A lesson is "future" only if it's not clickable at all
                
                return (
                  <button
                    key={levelLesson.id}
                    onClick={() => isClickable && handleLessonNavigation(levelLesson)}
                    disabled={!isClickable}
                    className={`relative w-12 h-12 transition-opacity ${
                      isCurrentLesson
                        ? 'cursor-pointer' // Current lesson - no opacity changes
                        : isCompleted
                          ? 'cursor-pointer hover:opacity-90' // Completed lessons - subtle hover
                          : isClickable 
                            ? 'cursor-pointer hover:opacity-80' 
                            : 'cursor-not-allowed opacity-60'
                    }`}
                    title={isFutureLesson ? "Complete previous lessons first" : ""}
                  >
                    <img 
                      src={hexagonImage}
                      alt={`Lesson ${levelLesson.lesson_number}`}
                      className="w-full h-full object-contain"
                    />
                    {/* Only show number if lesson is not completed */}
                    {showNumber && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-lg font-semibold text-white">
                          {levelLesson.lesson_number}
                        </span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Next Step Button */}
            <div className="relative group">
              <ActionButton
                onClick={handleNextStep}
                disabled={!nextLesson || !canProceed}
                showArrow={true}
              >
                Next Step
              </ActionButton>
              {/* Tooltip for disabled state */}
              {!canProceed && (
                <>
                  {/* sr-only gives screen reader users the same hint keyboard users can't see via hover */}
                  <span id="next-step-tooltip" className="sr-only">Complete all To Do items to move on to the next step</span>
                  <div aria-hidden="true" className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10 border border-[#2a2a2a]" style={{ backgroundColor: '#1f1f1f' }}>
                    Complete To Dos to move on to the next step
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent" style={{ borderTopColor: '#1f1f1f' }}></div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex">
        {/* Main Content Area */}
        <div className="w-full px-4 md:px-[90px] pt-8 md:pt-12 pb-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Lesson Content */}
          <div className={`lg:col-span-2 transition-opacity duration-200 ${lessonTransitioning ? 'opacity-60' : 'opacity-100'}`}>
            {/* Content based on lesson type */}
            {/* Vimeo links resume via LessonVideoPlayer; other hosts keep a static iframe. */}
            {(!lesson.lesson_type || lesson.lesson_type === 'video') && lesson.video_link && (
              <div className="mb-8">
                {isVimeoUrl(lesson.video_link) ? (
                  <LessonVideoPlayer
                    videoLink={lesson.video_link}
                    title={lesson.title}
                    userId={user?.uid}
                    lessonId={lessonId}
                    firestorePositionSeconds={getLessonProgress(lessonId)?.videoPositionSeconds}
                    firestoreDurationSeconds={getLessonProgress(lessonId)?.videoDurationSeconds}
                    firestorePositionUpdatedAt={getLessonProgress(lessonId)?.videoPositionUpdatedAt}
                  />
                ) : (
                  <div className="relative w-full bg-black rounded-lg overflow-hidden" style={{ paddingBottom: '56.25%' }}>
                    <iframe
                      src={lesson.video_link}
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                      className="absolute inset-0 w-full h-full"
                      title={lesson.title}
                    ></iframe>
                  </div>
                )}
              </div>
            )}

            {lesson.lesson_type === 'graphic' && lesson.graphic_link && (
              <div className="mb-8">
                {/* Button wraps the image so it is keyboard-operable */}
                <button
                  ref={lightboxTriggerRef}
                  type="button"
                  className="relative w-full rounded-lg overflow-hidden hover:opacity-90 transition-opacity group border focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  style={{ backgroundColor: themeColors.card, borderColor: themeColors.border }}
                  onClick={openLightbox}
                  aria-label={`Expand image: ${lesson.headline}`}
                >
                  <img
                    src={lesson.graphic_link}
                    alt={lesson.headline}
                    className="w-full h-auto object-contain"
                  />
                  {/* Expand icon overlay — decorative, button label conveys the action */}
                  <div aria-hidden="true" className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-full p-3 shadow-lg border" style={{ backgroundColor: themeColors.card, borderColor: themeColors.border }}>
                      <svg className="w-6 h-6" style={{ color: themeColors.textSecondary }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                      </svg>
                    </div>
                  </div>
                </button>
              </div>
            )}

            {lesson.lesson_type === 'excerpt' && lesson.excerpt && (
              <>
                {/* Lesson Headline and Description - above excerpt for excerpt lessons */}
                <HeadlineSmall className="mb-4" style={{ color: themeColors.textPrimary }}>{lesson.headline}</HeadlineSmall>
                <div className="mb-6 lesson-content" style={{ color: themeColors.textPrimary }}>
                  <BodyLarge className="!text-transparent" style={{ color: themeColors.textPrimary }} dangerouslySetInnerHTML={renderMarkdownToHTML(lesson.description)} />
                </div>
                
                {/* Excerpt Display */}
                <div className="mb-8">
                  <div className="w-full rounded-lg p-8 border" style={{ backgroundColor: themeColors.excerptBox, borderColor: themeColors.border }}>
                    <div className="max-h-96 overflow-y-auto prose prose-gray max-w-none">
                      <div 
                        className="leading-relaxed lesson-content"
                        style={{ color: themeColors.textPrimary }}
                        dangerouslySetInnerHTML={renderMarkdownToHTML(lesson.excerpt)}
                      />
                    </div>
                  </div>
                </div>
                {/* Ensure markdown-rendered content uses theme colors */}
                <style>{`
                  .lesson-content h4 {
                    color: ${themeColors.textPrimary} !important;
                  }
                  .lesson-content p {
                    color: ${themeColors.textPrimary} !important;
                  }
                  .lesson-content li {
                    color: ${themeColors.textPrimary} !important;
                  }
                  .lesson-content strong {
                    color: ${themeColors.textPrimary} !important;
                  }
                  .lesson-content em {
                    color: ${themeColors.textPrimary} !important;
                  }
                  .lesson-content u {
                    color: ${themeColors.textPrimary} !important;
                  }
                  .lesson-content blockquote {
                    color: ${themeColors.textPrimary} !important;
                  }
                  .lesson-content * {
                    color: ${themeColors.textPrimary} !important;
                  }
                `}</style>
              </>
            )}

            {/* Text lessons don't have a content area - headline and description go right below title */}

            {/* Lesson Headline and Description - displayed below content for non-excerpt lessons */}
            {lesson.lesson_type !== 'excerpt' && (
              <>
                <HeadlineSmall className="mb-4" style={{ color: themeColors.textPrimary }}>{lesson.headline}</HeadlineSmall>
                <div className="mb-6 lesson-content" style={{ color: themeColors.textPrimary }}>
                  <BodyLarge className="!text-transparent" style={{ color: themeColors.textPrimary }} dangerouslySetInnerHTML={renderMarkdownToHTML(lesson.description)} />
                </div>
                {/* Ensure markdown-rendered content uses theme colors */}
                <style>{`
                  .lesson-content h4 {
                    color: ${themeColors.textPrimary} !important;
                  }
                  .lesson-content p {
                    color: ${themeColors.textPrimary} !important;
                  }
                  .lesson-content li {
                    color: ${themeColors.textPrimary} !important;
                  }
                  .lesson-content strong {
                    color: ${themeColors.textPrimary} !important;
                  }
                  .lesson-content em {
                    color: ${themeColors.textPrimary} !important;
                  }
                  .lesson-content u {
                    color: ${themeColors.textPrimary} !important;
                  }
                  .lesson-content blockquote {
                    color: ${themeColors.textPrimary} !important;
                  }
                  .lesson-content * {
                    color: ${themeColors.textPrimary} !important;
                  }
                `}</style>
              </>
            )}
          </div>

          {/* Right Column - To Do List */}
          <div className="lg:col-span-1">
            <div className="p-6 rounded-lg sticky top-[150px] z-30 border" style={{ backgroundColor: themeColors.todoSidebar, borderColor: themeColors.border }}>
              <HeadlineSmall className="mb-4" style={{ color: themeColors.textPrimary }}>To Do:</HeadlineSmall>
              <BodyMedium className="mb-6" style={{ color: themeColors.textPrimary }}>
                The path to writing a great story is as simple as following these steps:
              </BodyMedium>

              {/* Checklist */}
              <div className="space-y-4">
                {lesson.todos && lesson.todos.map((task, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id={`todo-item-${index}`}
                      aria-label={task}
                      checked={isTaskCompleted(index)}
                      onChange={() => handleTaskToggle(index)}
                      className="w-10 h-10 rounded focus:ring-2 cursor-pointer flex-shrink-0 border-2"
                      style={{
                        accentColor: themeColors.accent,
                        minWidth: '40px',
                        minHeight: '40px',
                        maxWidth: '40px',
                        maxHeight: '40px',
                        width: '40px',
                        height: '40px',
                        appearance: 'auto',
                        WebkitAppearance: 'checkbox',
                        backgroundColor: isTaskCompleted(index) ? themeColors.accent : themeColors.card,
                        borderColor: isTaskCompleted(index) ? themeColors.accent : themeColors.border,
                        focusRingColor: themeColors.accent
                      }}
                    />
                    <BodyLarge className={`${
                      isTaskCompleted(index) ? 'line-through' : ''
                    }`} style={{ 
                      color: isTaskCompleted(index) ? themeColors.textTertiary : themeColors.textPrimary 
                    }}>
                      {task}
                    </BodyLarge>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Next Step Button - Bottom of page */}
        <div className="md:hidden mt-8 flex justify-center">
          <div className="relative group w-full max-w-sm">
            <ActionButton
              onClick={handleNextStep}
              disabled={!nextLesson || !canProceed}
              showArrow={true}
              fullWidth={true}
              size="lg"
            >
              Next Step
            </ActionButton>
            {/* Tooltip for disabled state */}
            {!canProceed && (
              <>
                {/* sr-only gives screen reader users the same hint as the hover tooltip */}
                <span className="sr-only">Complete all To Do items to move on to the next step</span>
                <div aria-hidden="true" className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10 border border-[#2a2a2a]" style={{ backgroundColor: '#1f1f1f' }}>
                  Complete To Dos to move on to the next step
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent" style={{ borderTopColor: '#1f1f1f' }}></div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox for graphics */}
      {lightboxOpen && lesson?.lesson_type === 'graphic' && lesson?.graphic_link && (
        <div 
          role="dialog"
          aria-modal="true"
          aria-label={`Expanded image: ${lesson?.headline || 'graphic'}`}
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
          onClick={closeLightbox}
          onKeyDown={(e) => {
            /* Contain Tab within the dialog — only the close button is focusable */
            if (e.key === 'Tab') {
              e.preventDefault();
              if (lightboxCloseRef.current) lightboxCloseRef.current.focus();
            }
          }}
        >
          <div className="relative max-w-[90%] max-h-[90%]">
            <img
              src={lesson.graphic_link}
              alt={lesson.headline}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            {/* Close button */}
            <button
              ref={lightboxCloseRef}
              onClick={closeLightbox}
              className="absolute top-4 right-4 text-white hover:text-gray-300 bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 focus:outline-none focus:ring-2 focus:ring-white"
              aria-label="Close lightbox"
            >
              <svg aria-hidden="true" className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Lesson;
